document.addEventListener('DOMContentLoaded', () => {
  const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
  let revealObserver = null;

  const activateRevealNodes = (root = document) => {
    const nodes = root.querySelectorAll ? root.querySelectorAll('[data-reveal]') : [];

    if (inDesignMode) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('is-visible');
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      );
    }

    nodes.forEach((node) => revealObserver.observe(node));
  };

  const nav = document.querySelector('[data-site-header]');
  const setScrolled = () => {
    if (nav) nav.classList.toggle('scrolled', window.scrollY > 20);
  };
  setScrolled();
  window.addEventListener('scroll', setScrolled);

  activateRevealNodes();

  if (inDesignMode) {
    document.addEventListener('shopify:section:load', (event) => activateRevealNodes(event.target));
    document.addEventListener('shopify:block:select', (event) => activateRevealNodes(event.target));
  }

  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  document.querySelectorAll('[data-blog-filter]').forEach((button) => {
    button.addEventListener('click', () => {
      const group = button.closest('[data-blog-filters]');
      const wrapper = document.querySelector('[data-blog-grid]');
      if (!group || !wrapper) return;
      group.querySelectorAll('[data-blog-filter]').forEach((node) => node.classList.remove('active'));
      button.classList.add('active');
      const tag = button.dataset.blogFilter;
      wrapper.querySelectorAll('[data-article-card]').forEach((card) => {
        const tags = (card.dataset.tags || '').split('|');
        const visible = tag === 'all' || tags.includes(tag);
        card.style.display = visible ? 'flex' : 'none';
      });
    });
  });

  const search = document.querySelector('[data-blog-search]');
  if (search) {
    search.addEventListener('input', () => {
      const query = search.value.trim().toLowerCase();
      document.querySelectorAll('[data-article-card]').forEach((card) => {
        const visible = card.textContent.toLowerCase().includes(query);
        card.style.display = visible ? 'flex' : 'none';
      });
    });
  }

  const mobileToggle = document.querySelector('[data-mobile-nav-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (mobileToggle && mobileNav) {
    mobileToggle.addEventListener('click', () => {
      const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
      mobileToggle.setAttribute('aria-expanded', String(!expanded));
      mobileNav.hidden = expanded;
    });

    mobileNav.querySelectorAll('[data-mobile-dropdown]').forEach((item) => {
      const button = item.querySelector('.mobile-nav__dropdown-toggle');
      const dropdown = item.querySelector('.mobile-nav__dropdown');
      if (button && dropdown) {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          dropdown.hidden = expanded;
        });
      }
    });

    mobileNav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        mobileToggle.setAttribute('aria-expanded', 'false');
        mobileNav.hidden = true;
      });
    });
  }

  // Prepare larger photos locally as soon as they are selected, so submitting
  // does not need to transfer three full-size camera originals as base64.
  const contactPhotoCache = new WeakMap();
  const optimizeContactPhoto = async (file) => {
    if (file.size <= 512 * 1024 || !/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
    let bitmap;
    try {
      if (typeof window.createImageBitmap !== 'function') return file;
      bitmap = await window.createImageBitmap(file);
      const scale = Math.min(1, 1920 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return file;
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      let transparent = false;
      if (file.type !== 'image/jpeg') {
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 255) { transparent = true; break; }
        }
      }
      const type = transparent ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.88));
      if (!blob || blob.size >= file.size * 0.9) return file;
      const extension = transparent ? '.png' : '.jpg';
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + extension, { type });
    } catch {
      // Keep the selected photo intact if this browser cannot optimize it.
      return file;
    } finally {
      if (bitmap && typeof bitmap.close === 'function') bitmap.close();
    }
  };
  const prepareContactPhoto = (file) => {
    if (contactPhotoCache.has(file)) return contactPhotoCache.get(file);
    const prepared = optimizeContactPhoto(file).then((photo) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = String(reader.result || '');
        const base64 = value.includes(',') ? value.split(',')[1] : '';
        if (!base64) return reject(new Error('A photo could not be read. Please select it again.'));
        resolve({ filename: photo.name, mime: photo.type || 'image/png', data_base64: base64 });
      };
      reader.onerror = reader.onabort = () => reject(
        new Error('A photo could not be read. Please select it again.')
      );
      reader.readAsDataURL(photo);
    }));
    // Selection-time preparation must not create unhandled promise rejections.
    prepared.catch(() => {});
    contactPhotoCache.set(file, prepared);
    return prepared;
  };
  document.querySelectorAll('[data-contact-lead-form] input[type="file"][name="attachment"]').forEach((input) => {
    input.addEventListener('change', () => {
      Array.from(input.files || []).slice(0, 3).forEach((file) => {
        if (file.size <= 8 * 1024 * 1024 && (!file.type || file.type.startsWith('image/'))) {
          prepareContactPhoto(file);
        }
      });
    });
  });

  // Capture before Shopify's submit handlers so they cannot navigate away
  // while the Feishu request is still pending. Resume the normal Shopify
  // submission, including its validation and captcha, after a confirmed write.
  window.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-contact-lead-form]')) return;
    if (form.dataset.leadMirrorComplete === 'true') return;
    const apiUrl = form.dataset.contactApiUrl;
    if (!apiUrl) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.leadSubmitting === 'true') return;
    form.dataset.leadSubmitting = 'true';

    const submitButton = form.querySelector('button[type="submit"]');
    const originalLabel = submitButton ? submitButton.textContent : '';
    let status = form.querySelector('[data-contact-lead-status]');
    if (!status) {
      status = document.createElement('div');
      status.dataset.contactLeadStatus = 'true';
      status.className = 'form-status form-status--error';
      status.setAttribute('role', 'alert');
      status.setAttribute('tabindex', '-1');
      form.insertBefore(status, submitButton);
    }
    status.hidden = true;
    status.textContent = '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    let timeoutId;
    let saved = false;
    try {
      const formData = new FormData(form);
      const fileInput = form.querySelector('input[type="file"][name="attachment"]');
      const files = fileInput ? Array.from(fileInput.files || []) : [];
      if (files.length > 3) throw new Error('Choose up to 3 images, then try again.');
      if (files.some((file) => file.size > 8 * 1024 * 1024)) {
        throw new Error('Each image must be 8MB or smaller. Please choose a smaller image.');
      }
      if (files.some((file) => file.type && !file.type.startsWith('image/'))) {
        throw new Error('Please choose image files for your product photos.');
      }

      if (submitButton && files.length) submitButton.textContent = 'Preparing photos...';
      const attachment = await Promise.all(files.map(prepareContactPhoto));
      const phone = String(formData.get('contact[phone]') || '').trim();

      const payload = {
        source: form.dataset.contactSource || 'shopify_storefront',
        shop_domain: window.location.hostname,
        page_url: window.location.href,
        page_title: document.title,
        name: formData.get('contact[name]') || '',
        email: formData.get('contact[email]') || '',
        ...(phone ? { phone } : {}),
        monthly_orders: formData.get('contact[monthly_orders]') || '',
        need: formData.get('contact[need]') || '',
        message: formData.get('contact[body]') || '',
        customer_link_url: String(formData.get('contact[link_url]') || '').trim(),
        attachment,
        referrer: document.referrer || '',
        user_agent: window.navigator.userAgent,
        payload: {
          source_page: formData.get('contact[source_page]') || '',
          source_page_title: formData.get('contact[source_page_title]') || '',
        },
      };

      const controller = new AbortController();
      if (submitButton) submitButton.textContent = attachment.length ? 'Uploading photos...' : 'Sending request...';
      timeoutId = window.setTimeout(() => controller.abort(), attachment.length ? 60000 : 30000);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true || typeof result.record_id !== 'string' || !result.record_id.trim()) {
        console.warn('Contact request was not confirmed.', response.status, result.error || 'missing_record_id');
        throw new Error('We could not send your request. Your details are still here. Please try again.');
      }

      form.dataset.leadMirrorComplete = 'true';
      form.dataset.leadRecordId = result.record_id;
      saved = true;
    } catch (error) {
      status.textContent = error.name === 'AbortError'
        ? 'Sending took longer than expected. Your details are still here. Please wait a moment before retrying.'
        : error instanceof SyntaxError || error instanceof TypeError
          ? 'We could not send your request. Your details are still here. Please check your connection and try again.'
          : error.message || 'We could not send your request. Please try again.';
      status.hidden = false;
      status.focus();
    } finally {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      delete form.dataset.leadSubmitting;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalLabel;
      }
    }

    if (saved) form.requestSubmit(submitButton || undefined);
  }, true);

});
